import { useState, useContext } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useValidateEmail } from '@/hooks/use-validate-email';
import { validateOtp } from '@/services/auth/validate-otp';
import { getOtp } from '@/services/auth/get-otp';
import { authDiscover } from '@/services/auth/auth-discover';
import { useNotification } from '@/components/notifications';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/loading-spinner';
import { useAuth0Safe, Auth0NotConfiguredError } from '@/context/auth0-context';
import { AuthContext } from '@/context/auth-context';
import { Mail } from 'lucide-react';

const MicrosoftIcon = () => (
  <svg width='16' height='16' viewBox='0 0 21 21' fill='none'>
    <rect x='1' y='1' width='9' height='9' fill='#F25022' />
    <rect x='11' y='1' width='9' height='9' fill='#7FBA00' />
    <rect x='1' y='11' width='9' height='9' fill='#00A4EF' />
    <rect x='11' y='11' width='9' height='9' fill='#FFB900' />
  </svg>
);

export const Login = () => {
  const notification = useNotification();
  const [code, setCode] = useState('');
  const [isOtpVisible, setIsOtpVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [ssoMethod, setSsoMethod] = useState<{
    auth0_org_id: string;
    connection?: string;
    org_name?: string;
    org_logo_url?: string;
  } | null>(null);
  const [showMethodChoice, setShowMethodChoice] = useState(false);
  const navigate = useNavigate();
  const { email, setEmail, isEmailValid } = useValidateEmail();
  const { loginWithRedirect } = useAuth0Safe();
  const { refetchAuth } = useContext(AuthContext);

  const buttonLabel = isOtpVisible ? 'Log in' : 'Continue';

  const handleButtonClick = async () => {
    if (isOtpVisible) {
      // Step 3: Validate OTP code
      if (!email || !code) {
        return;
      }
      setIsLoading(true);

      try {
        const result = await validateOtp({ email, code });
        if (result?.detail) {
          notification.error(result.detail);
          setIsLoading(false);
          return;
        }
        const authResult = await refetchAuth();
        if (
          authResult?.userResponse?.detail ||
          authResult?.tokenResponse?.detail
        ) {
          notification.error('Could not load your session. Please try again.');
          setIsLoading(false);
          return;
        }
        navigate('/');
      } catch (error) {
        console.error('Validate OTP error:', error);
        notification.error('An unexpected error occurred');
        setIsLoading(false);
      }
    } else {
      // Step 1: Discover auth method for this email
      if (!email) {
        return;
      }
      setIsLoading(true);

      try {
        const discoverResult = await authDiscover(email);

        if (discoverResult?.detail) {
          // Discovery failed — fall back to OTP flow
          console.warn(
            'Auth discover failed, falling back to OTP:',
            discoverResult.detail,
          );
          await handleOtpFlow();
          return;
        }

        const methods = discoverResult.data?.methods || [];
        const foundSso = methods.find((m) => m.type === 'sso');
        const hasOtp = methods.some((m) => m.type === 'otp');

        if (foundSso?.auth0_org_id && hasOtp) {
          // Both SSO and OTP available — show method choice screen
          setSsoMethod({
            auth0_org_id: foundSso.auth0_org_id,
            connection: foundSso.connection,
            org_name: foundSso.org_name,
            org_logo_url: foundSso.org_logo_url,
          });
          setShowMethodChoice(true);
          setIsLoading(false);
          return;
        }

        if (foundSso?.auth0_org_id) {
          // SSO only: redirect to Auth0 with organization and connection
          await loginWithRedirect({
            authorizationParams: {
              organization: foundSso.auth0_org_id,
              connection: foundSso.connection,
              login_hint: email,
            },
          });
          // loginWithRedirect navigates away — loading stays true
          return;
        }

        // OTP only: send OTP code
        await handleOtpFlow();
      } catch (error) {
        console.error('Auth discover error:', error);
        if (error instanceof Auth0NotConfiguredError) {
          notification.error(
            'SSO is unavailable in this environment. Please try email code or contact support.',
          );
        } else {
          notification.error('An unexpected error occurred');
        }
        setIsLoading(false);
      }
    }
  };

  const handleSsoLogin = async () => {
    if (!ssoMethod) return;
    setIsLoading(true);
    try {
      await loginWithRedirect({
        authorizationParams: {
          organization: ssoMethod.auth0_org_id,
          connection: ssoMethod.connection,
          login_hint: email,
        },
      });
    } catch (error) {
      console.error('SSO login error:', error);
      if (error instanceof Auth0NotConfiguredError) {
        notification.error(
          'SSO is unavailable in this environment. Please try email code or contact support.',
        );
      } else {
        notification.error(
          'SSO login failed. Please try email code or contact support.',
        );
      }
      setIsLoading(false);
    }
  };

  const handleOtpFromChoice = async () => {
    setShowMethodChoice(false);
    setSsoMethod(null);
    setIsLoading(true);
    await handleOtpFlow();
  };

  const handleOtpFlow = async () => {
    try {
      const result = await getOtp(email);

      if (result?.detail) {
        if (
          typeof result.detail === 'string' &&
          result.detail.toLowerCase().includes('user does not exist')
        ) {
          notification.error(
            'No account found for this email. Please contact your administrator to register.',
          );
        } else {
          notification.error(result.detail);
        }
        setIsLoading(false);
        return;
      }
      setIsOtpVisible(true);
    } catch (error) {
      console.error('Get OTP error:', error);
      notification.error('An unexpected error occurred');
    }
    setIsLoading(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleButtonClick();
    }
  };

  if (showMethodChoice && ssoMethod) {
    return (
      <div className='flex justify-center items-center h-full w-full'>
        <Card>
          <CardContent className='flex flex-col gap-6'>
            <div className='flex flex-col gap-6 items-center w-80'>
              {ssoMethod.org_logo_url && (
                <img
                  src={ssoMethod.org_logo_url}
                  alt={ssoMethod.org_name || 'Organization'}
                  className='h-10 w-auto object-contain'
                />
              )}
              <div className='flex flex-col items-center gap-1'>
                <h3 className='text-xl font-semibold text-foreground'>
                  Choose sign-in method
                </h3>
                <p className='text-sm text-muted-foreground'>{email}</p>
              </div>
              <Button
                onClick={handleSsoLogin}
                disabled={isLoading}
                className='w-full gap-2'
                id='login-button-sso'
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner />
                    Loading...
                  </>
                ) : (
                  <>
                    <MicrosoftIcon />
                    Sign in with Microsoft
                    {ssoMethod.org_name ? ` (${ssoMethod.org_name})` : ''}
                  </>
                )}
              </Button>
              <div className='flex items-center w-full gap-3'>
                <div className='flex-1 h-px bg-border' />
                <span className='text-xs text-muted-foreground'>or</span>
                <div className='flex-1 h-px bg-border' />
              </div>
              <Button
                onClick={handleOtpFromChoice}
                disabled={isLoading}
                variant='outline'
                className='w-full gap-2'
                id='login-button-otp'
              >
                <Mail className='h-4 w-4' />
                Sign in with email code
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className='flex justify-center items-center h-full w-full'>
      <Card>
        <CardContent className='flex flex-col gap-6'>
          <div className='flex flex-col gap-6 items-center w-80'>
            {isOtpVisible ? (
              <div className='flex flex-col items-center'>
                <h3 className='text-xl font-semibold text-foreground'>
                  Log in
                </h3>
                <p className='text-base text-muted-foreground'>
                  Enter the code you received on your email
                </p>
                <p className='text-sm text-muted-foreground underline'>
                  {email}
                </p>
              </div>
            ) : (
              <div className='flex flex-col items-center gap-2'>
                <h3 className='text-xl font-semibold text-foreground'>
                  Log in
                </h3>
                <p className='text-base text-muted-foreground'>
                  Welcome back! Sign in with your work email to continue.
                </p>
              </div>
            )}

            {isOtpVisible ? (
              <div className='flex w-full items-center gap-2'>
                <Input
                  className='w-auto flex-1'
                  value={code}
                  placeholder='Code'
                  id='login-input-code'
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
            ) : (
              <div className='flex w-full items-center gap-2'>
                <Input
                  className='w-auto flex-1'
                  error={isEmailValid ? '' : 'Invalid email'}
                  name='email'
                  value={email}
                  placeholder='Email'
                  id='login-input-email'
                  type='email'
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>
            )}
          </div>
          <Button
            aria-label={buttonLabel}
            disabled={!isEmailValid || isLoading}
            id={isOtpVisible ? 'login-button-login' : 'login-button-continue'}
            onClick={handleButtonClick}
            className='w-full'
          >
            {isLoading ? (
              <>
                <LoadingSpinner />
                Loading...
              </>
            ) : (
              buttonLabel
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
